import { airWalker, type AirWalk } from './holeAir';
import { holesOfLine, lineEnv, type LineEnv } from './holeLines';
import { slotRuns, type HoleSlot } from './holeSlots';
import { loadSolids, type Solid } from './partSolids';
import { isLeftRight, type PartItem, type Side } from './partsRun';
import type { StepHole } from './stepHoles';
import type { HolesContext } from './stepHolesPrompt';
import type { MeshOf } from './stepHolesRun';

// WHERE A SLOT'S HOLE CAN GO — the outline, where the two parts really touch, and every place in it
// whose line works, tried before the call is asked rather than found wrong after it answered.
//
// THE OUTLINE IS MEASURED ON THE MESHES. The slot comes with the overlap of the two parts' BOXES
// across the face (see holeSlots), and a box is where a part could be: a side rail angled in its box
// covers 22 mm of its 33.5, and its end met the frame on the bottom 30 mm of its 61 — the rest was
// air, outlined in red and walked for nothing. So that overlap is walked once for where the next
// part's wood meets the entry part's (LineHoles.touches), and the outline is what does.
//
// THEN THE PLACES IN IT. A curved rail leans inside its box and dips under the seat, so even where
// the parts touch, the screw may cross air before the seat: the call placed holes there, and the line
// said so only once they were drilled. So the outline is walked on a grid and each point is run
// through the very line the answer will be (holesOfLine): on the wood, nothing but wood to the part
// it stops in, and far enough into it. What passes is drawn green on the face's picture (see
// jointShots) and the call is told to place the holes there.
//
// THE WHOLE HOLE, NOT ITS CENTRE. The line follows the hole's middle, and a dowel is 8 mm across: a
// place 11.5 mm off the bottom of a rail whose wood starts at 9 passed, and the dowel's side broke out
// for 7 mm. So each place that passes is drilled in thought and walked as the joint view walks the
// holes it shows (see holeAir) — centre and edge, every hole of the line — and green only if that
// finds no air. One test, so the green and the hole's status cannot disagree.
//
// AND THE HOLE ACROSS IT: a screw into a barrel nut makes the nut's cross hole too, 10 mm wide at the
// screw's height in the rail, and a place the screw's 6 mm fitted put the nut's side a fraction of a
// millimetre inside the rail's curved underside. So the line is run with its nut, and walked with it.
//
// AND EVERY HAND IT WILL BE DRILLED ON: a slot asked for one rail is made into the other too (see
// foldMirrors), and the other rail's line reaches the frame both meet at the reflected place, which
// the frame does not promise to have the same wood.

// At most this many points along each side of the outline, and one a millimetre at the finest.
const PER_SIDE = 60;
const half = (mm: number) => Math.round(mm * 2) / 2;

// THE PLACES THAT WORK, in the face's own figures (mm from the edges the outline's ranges count
// from), each the middle of a du × dv cell. Null where it could not be checked — no outline, or a
// line nothing can be said of (see LineHoles.fits) — and the call is then told nothing about it.
export type SlotArea = { du: number; dv: number; ok: [number, number][] };

type Outline = NonNullable<HoleSlot['patch']>;
type Cell = { u: number; v: number; fits: boolean; meets: boolean };

// AN OUTLINE WALKED: at each point, whether its line works and whether the next part meets the entry
// part's wood along it — on EVERY hand the line will be run for (see lineHoles). Null where a line
// nothing can be said of. With `air`, a line works only if its holes, walked whole, find no air.
function walk(slot: HoleSlot, p: Outline, env: LineEnv, items: PartItem[], air?: AirWalk) {
  const nu = Math.min(PER_SIDE, Math.max(1, Math.ceil(p.u.hi - p.u.lo)));
  const nv = Math.min(PER_SIDE, Math.max(1, Math.ceil(p.v.hi - p.v.lo)));
  const du = (p.u.hi - p.u.lo) / nu;
  const dv = (p.v.hi - p.v.lo) / nv;
  const hands: (Side | undefined)[] = !isLeftRight(items[Number(slot.part)]) ? [undefined]
    : slot.side ? [slot.side, ...(slot.mirror ? [slot.mirror.side] : [])] : ['left', 'right'];
  const cells: Cell[] = [];
  for (let i = 0; i < nu; i++) {
    for (let j = 0; j < nv; j++) {
      const u = half(p.u.lo + (i + 0.5) * du);
      const v = half(p.v.lo + (j + 0.5) * dv);
      let fits = true;
      let meets = true;
      for (const hand of hands) {
        const made = holesOfLine({
          at: slot.at, via: slot.via, part: slot.part, side: slot.side, face: slot.face,
          u: { from: p.u.from, mm: u }, v: { from: p.v.from, mm: v }, nut: slot.nut,
        }, env, (id) => id, hand);
        if (made.fits == null) return null;
        fits &&= made.fits;
        // One line, so its holes share one; ids only so the walk can say which hole a note is about.
        if (fits && air) fits = !air(made.holes.map((h, k) => ({ ...h, id: `h${k}`, line: 'h' } as StepHole)), (id) => id).size;
        meets &&= !!slot.next && made.touches.includes(slot.next);
      }
      cells.push({ u, v, fits, meets });
    }
  }
  return { du, dv, cells };
}

// WHERE THE PARTS TOUCH, as an outline: the cells where they meet, and the box overlap no further.
// Null where they meet nowhere in it.
function contactOf(p: Outline, walked: { du: number; dv: number; cells: Cell[] }): Outline | null {
  const on = walked.cells.filter((c) => c.meets);
  if (!on.length) return null;
  const range = (r: Outline['u'], at: number[], d: number) => ({
    from: r.from,
    lo: Math.max(r.lo, Math.floor((Math.min(...at) - d / 2) * 2) / 2),
    hi: Math.min(r.hi, Math.ceil((Math.max(...at) + d / 2) * 2) / 2),
  });
  return { u: range(p.u, on.map((c) => c.u), walked.du), v: range(p.v, on.map((c) => c.v), walked.dv) };
}

// A SLOT'S OUTLINE AND ITS AREA. Where no line can be checked (no length for the hardware), the box
// overlap stays, unwalked, as it was handed over. Where the meshes meet nowhere in it there is no
// outline: the call is told so, and places it from the drawing.
function outlineOf(slot: HoleSlot, env: LineEnv, items: PartItem[], air: AirWalk): Pick<HoleSlot, 'patch' | 'area'> {
  const boxes = slot.patch;
  if (!boxes) return { patch: null, area: null };
  const first = walk(slot, boxes, env, items);
  if (!first) return { patch: boxes, area: null };
  const touch = contactOf(boxes, first);
  if (!touch) return { patch: null, area: null };
  const inside = walk(slot, touch, env, items, air);
  if (!inside) return { patch: touch, area: null };
  const ok = inside.cells.filter((c) => c.fits).map((c): [number, number] => [c.u, c.v]);
  return { patch: touch, area: { du: inside.du, dv: inside.dv, ok } };
}

// THE GROUP'S SLOTS, each with its outline and area — worked out once for the slots that share one.
function withAreas(slots: HoleSlot[], env: LineEnv, items: PartItem[], air: AirWalk): HoleSlot[] {
  const byRun = new Map<HoleSlot, Pick<HoleSlot, 'patch' | 'area'>>();
  for (const run of slotRuns(slots)) {
    const got = outlineOf(run[0], env, items, air);
    run.forEach((sl) => byRun.set(sl, got));
  }
  return slots.map((sl) => ({ ...sl, ...byRun.get(sl) }));
}

// WHAT A GROUP'S LINES ARE RUN THROUGH: its parts as the product built them, their solids, and the
// hardware table's word on what is gone all the way through, where its rows give one.
export const lineEnvOf = (ctx: HolesContext, meshOf: MeshOf | undefined, solids: Map<string, Solid>) => {
  const told = ctx.rows.flatMap((r) => r.through);
  return lineEnv(ctx.items, ctx.models, ctx.drilled, meshOf ?? (() => null), solids, told.length ? told : null);
};

// THE GROUP AS ASKED: its slots with the places in each outline where the line works (see
// holeArea), on its parts' solids — the run's and the badge's one way of making it.
// `undrilled`: on the parts as they stood before their first bore — the badge, showing what a re-run
// would be asked; the run has undrilled them already.
export async function withSlotAreas(
  ctx: HolesContext, meshOf: MeshOf | undefined, solids = new Map<string, Solid>(), tried?: Set<string>, undrilled = false,
) {
  await loadSolids(ctx.drilled, meshOf, ctx.items, solids, tried, undrilled);
  // Every component's mesh, into the same map: the air walk looks for wood in all of them.
  const air = await airWalker(ctx.items, ctx.models, meshOf ?? (() => null), solids, tried, undrilled);
  return { ...ctx, slots: withAreas(ctx.slots, lineEnvOf(ctx, meshOf, solids), ctx.items, air) };
}
